import type { DrawnHead, DrawnNote, DrawnStaff } from './drawn';
import { ENGRAVING, glyphHorizontalBounds } from './smufl';

export interface TieSegment {
  si: number;
  fromId: string;
  toId?: string;
  head: number;
  segment: 'whole' | 'out' | 'in';
  x1: number;
  y1: number;
  cx: number;
  cy: number;
  x2: number;
  y2: number;
}

/** Per-head ties over the complete staff, split before pixel/page placement. */
export function staffTies(staff: DrawnStaff): TieSegment[] {
  const flat = staff.systems.flatMap((sys, si) =>
    sys.measures.flatMap((m) =>
      m.notes.map((n) => ({ n, si, bar: m.barIndex, x: m.x + n.x, measure: m })),
    ),
  );
  const heads = (n: DrawnNote): DrawnHead[] => n.heads ?? [{ ...n, dx: 0 }];
  const scale = (n: DrawnNote) => (n.grace ? ENGRAVING.graceScale : n.cue ? ENGRAVING.cueScale : 1);
  const result: TieSegment[] = [];
  for (const [index, from] of flat.entries())
    for (const [hi, head] of heads(from.n).entries()) {
      if (!head.tieToNext) continue;
      const voice = from.n.voice ?? 0;
      const later = flat.slice(index + 1).filter((o) => !o.n.grace && o.bar <= from.bar + 1);
      const samePitch = (h: DrawnHead) =>
        head.midi != null && h.midi != null ? h.midi === head.midi : h.y === head.y;
      const sameVoice = later.find((o) => (o.n.voice ?? 0) === voice);
      let to = sameVoice;
      let target = to && heads(to.n).find(samePitch);
      // A duplicated voice may be condensed to another voice at the next bar.
      // Never jump past a different pitch or a rest in an existing voice.
      if (!sameVoice) {
        to = later.find(
          (o) =>
            o.bar === from.bar + 1 && heads(o.n).some((h) => samePitch(h) && h.tieFromPrevious),
        );
        target = to && heads(to.n).find(samePitch);
      }
      if (to) {
        const restBefore = staff.systems
          .flatMap((sys) => sys.measures)
          .some(
            (m) =>
              m.barIndex >= from.bar &&
              m.barIndex <= to!.bar &&
              m.rests.some(
                (r) =>
                  (r.voice ?? 0) === voice &&
                  r.beat != null &&
                  (m.barIndex > from.bar || r.beat > from.n.beat) &&
                  (m.barIndex < to!.bar || r.beat <= to!.n.beat),
              ),
          );
        if (restBefore) {
          to = undefined;
          target = undefined;
        }
      }
      if (!target) to = undefined;
      const chord = heads(from.n);
      const stemUp = from.n.stem ? from.n.stem.y2 < from.n.stem.y1 : from.n.y >= 2;
      const multiVoice = new Set(from.measure.notes.map((n) => n.voice ?? 0)).size > 1;
      const above = multiVoice ? stemUp : chord.length > 1 ? hi >= chord.length / 2 : !stemUp;
      const dir = above ? -1 : 1;
      const hb = glyphHorizontalBounds(from.n.notehead);
      const x1 = from.x + head.dx + hb.right * scale(from.n) + 0.08;
      const y1 = head.y + dir * 0.3;
      const x2 =
        to && target
          ? to.x + target.dx + glyphHorizontalBounds(to.n.notehead).left * scale(to.n) - 0.08
          : x1 + 1.5;
      const y2 = target ? target.y + dir * 0.3 : y1;
      const add = (
        si: number,
        segment: TieSegment['segment'],
        xa: number,
        ya: number,
        xb: number,
        yb: number,
      ) => {
        if (xb <= xa) return;
        result.push({
          si,
          fromId: from.n.sourceId,
          ...(to ? { toId: to.n.sourceId } : {}),
          head: hi,
          segment,
          x1: xa,
          y1: ya,
          x2: xb,
          y2: yb,
          cx: (xa + xb) / 2,
          cy: (ya + yb) / 2 + dir * 0.9,
        });
      };
      if (to && to.si !== from.si) {
        add(from.si, 'out', x1, y1, staff.systems[from.si].width - 0.1, y1);
        add(to.si, 'in', 0.2, y2, x2, y2);
      } else add(from.si, 'whole', x1, y1, x2, y2);
    }
  return result;
}

export function tieVerticalBounds(tie: TieSegment): { top: number; bottom: number } {
  const ts = [0, 1];
  const d = tie.y1 - 2 * tie.cy + tie.y2;
  if (Math.abs(d) > 1e-12) ts.push((tie.y1 - tie.cy) / d);
  const ys = ts
    .filter((t) => t >= 0 && t <= 1)
    .map((t) => (1 - t) ** 2 * tie.y1 + 2 * t * (1 - t) * tie.cy + t ** 2 * tie.y2);
  return { top: Math.min(...ys), bottom: Math.max(...ys) };
}
